import { CheckOutlined, DesktopOutlined, UndoOutlined } from '@ant-design/icons';
import { Button, Drawer, Popconfirm, Radio, Select, Switch, Tooltip } from 'antd';
import type { ReactNode } from 'react';
import {
  type MenuType,
  type NavigationMode,
  type ThemeMode,
  timeZones,
} from '@/config/preferences';
import { useChangeLocale } from '@/hooks/useChangeLocale';
import { useShellText } from '@/hooks/useShellText';
import { localeOptions } from '@/locales/config';
import { useLayoutSettingsStore } from '@/stores/layoutSettingsStore';
import { type AppLocale, useLocaleStore } from '@/stores/localeStore';
import { usePreferenceStyles } from './preferenceStyles';

function PreviewChoices<T extends string>({
  label,
  options,
  value,
  onChange,
  showLabels = false,
}: {
  label: string;
  options: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
  showLabels?: boolean;
}) {
  const { styles } = usePreferenceStyles();
  return (
    <Radio.Group
      aria-label={label}
      className={styles.choices}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    >
      {options.map((option) => (
        <Tooltip title={option.label} trigger={['hover', 'focus']} key={option.value}>
          <Radio
            aria-label={option.label}
            value={option.value}
            className={`${styles.choice} ${showLabels ? styles.labeledChoice : ''}`}
            styles={{
              icon: {
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                opacity: 0,
                zIndex: 1,
              },
              label: { display: 'block', paddingInline: 0 },
              root: { marginInlineEnd: 0 },
            }}
          >
            <span aria-hidden className={styles.preview} data-preview={option.value}>
              {option.value === 'system' ? (
                <DesktopOutlined />
              ) : (
                <>
                  <span className="preview-side" />
                  <span className="preview-header" />
                  <span className="preview-lines" />
                </>
              )}
              {value === option.value && <CheckOutlined className="preview-check" />}
            </span>
            {showLabels && <span className={styles.choiceLabel}>{option.label}</span>}
          </Radio>
        </Tooltip>
      ))}
    </Radio.Group>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  const { styles } = usePreferenceStyles();
  return (
    <section className={styles.section}>
      <h3>{title}</h3>
      {children}
    </section>
  );
}

export function PreferencesDrawer({
  open,
  onClose,
  isPhone,
}: {
  open: boolean;
  onClose: () => void;
  isPhone: boolean;
}) {
  const t = useShellText();
  const { styles } = usePreferenceStyles();
  const settings = useLayoutSettingsStore((state) => state.settings);
  const setSettings = useLayoutSettingsStore((state) => state.setSettings);
  const reset = useLayoutSettingsStore((state) => state.resetSettings);
  const locale = useLocaleStore((state) => state.locale);
  const setLocale = useChangeLocale();
  const navigationMode = settings.navigationMode ?? 'side';
  const menuOptions: { value: MenuType; label: string }[] = [
    { value: 'single', label: t('单列菜单', 'Single menu') },
    { value: 'serviceGrid', label: t('服务网格菜单', 'Service grid') },
    ...(navigationMode === 'side'
      ? [
          { value: 'twoColumn' as const, label: t('双栏菜单', 'Two-column menu') },
          {
            value: 'splitServiceGrid' as const,
            label: t('双栏服务网格', 'Split service grid'),
          },
        ]
      : []),
  ];
  const colors = [
    ['#1677ff', t('蓝色', 'Blue')],
    ['#722ed1', t('紫色', 'Purple')],
    ['#13c2c2', t('青色', 'Cyan')],
    ['#52c41a', t('绿色', 'Green')],
    ['#eb2f96', t('粉色', 'Pink')],
    ['#f5222d', t('红色', 'Red')],
    ['#fa8c16', t('橙色', 'Orange')],
    ['#fadb14', t('黄色', 'Yellow')],
  ];
  return (
    <Drawer
      title={t('偏好设置', 'Preferences')}
      open={open}
      onClose={onClose}
      size={isPhone ? '100%' : 378}
      footer={
        <Popconfirm
          title={t('重置偏好设置？', 'Reset preferences?')}
          description={t(
            '恢复默认主题、导航、语言和时区。',
            'Restore the default theme, navigation, language and time zone.',
          )}
          onConfirm={() => {
            reset();
            setLocale('zh-CN');
          }}
        >
          <Button block icon={<UndoOutlined />}>
            {t('重置偏好设置', 'Reset preferences')}
          </Button>
        </Popconfirm>
      }
    >
      <div className={styles.content}>
        <Section title={t('外观', 'Appearance')}>
          <PreviewChoices<ThemeMode>
            showLabels={isPhone}
            label={t('主题模式', 'Theme mode')}
            value={settings.themeMode ?? 'light'}
            onChange={(themeMode) => setSettings({ themeMode })}
            options={[
              { value: 'light', label: t('浅色', 'Light') },
              { value: 'dark', label: t('深色', 'Dark') },
              { value: 'system', label: t('跟随系统', 'System') },
            ]}
          />
        </Section>
        <Section title={t('主题色', 'Theme color')}>
          <div className={styles.swatches}>
            {colors.map(([color, label]) => (
              <Tooltip title={label} key={color} trigger={['hover', 'focus']}>
                <Button
                  aria-label={label}
                  aria-pressed={settings.colorPrimary === color}
                  className={styles.swatch}
                  style={{
                    background: color,
                    color: ['#fadb14', '#faad14'].includes(color) ? '#141414' : '#fff',
                  }}
                  icon={settings.colorPrimary === color ? <CheckOutlined /> : undefined}
                  onClick={() => setSettings({ colorPrimary: color })}
                />
              </Tooltip>
            ))}
          </div>
        </Section>
        <hr />
        {!isPhone && (
          <Section title={t('导航模式', 'Navigation mode')}>
            <PreviewChoices<NavigationMode>
              showLabels={isPhone}
              label={t('导航模式', 'Navigation mode')}
              value={navigationMode}
              onChange={(next) => setSettings({ navigationMode: next })}
              options={[
                { value: 'side', label: t('侧边菜单', 'Side menu') },
                { value: 'top', label: t('顶部菜单', 'Top menu') },
                { value: 'mixed', label: t('混合菜单', 'Mixed menu') },
              ]}
            />
          </Section>
        )}
        {isPhone && (
          <p>
            {t(
              '部分布局设置仅在桌面端可用',
              'Some layout settings are only available on desktop',
            )}
          </p>
        )}
        {!isPhone && navigationMode !== 'top' && (
          <Section title={t('侧栏菜单样式', 'Sidebar style')}>
            <PreviewChoices<MenuType>
              showLabels={isPhone}
              label={t('侧栏菜单样式', 'Sidebar style')}
              value={settings.menuType ?? 'single'}
              onChange={(menuType) => setSettings({ menuType })}
              options={menuOptions}
            />
          </Section>
        )}
        <div className={styles.row}>
          <span>{t('显示页脚', 'Show footer')}</span>
          <Switch
            aria-label={t('显示页脚', 'Show footer')}
            checked={settings.footerRender !== false}
            onChange={(value) =>
              setSettings({ footerRender: value ? undefined : false })
            }
            size="small"
          />
        </div>
        <hr />
        <Section title={t('语言与地区', 'Language and region')}>
          <div className={styles.row}>
            <span>{t('界面语言', 'Interface language')}</span>
            <Select<AppLocale>
              aria-label={t('界面语言', 'Interface language')}
              value={locale}
              onChange={setLocale}
              options={localeOptions.map(({ key, label, emoji }) => ({
                value: key,
                label: `${emoji} ${label}`,
              }))}
            />
          </div>
          <div className={styles.row}>
            <span>{t('时区', 'Time zone')}</span>
            <Select
              aria-label={t('时区', 'Time zone')}
              value={settings.timeZone}
              onChange={(timeZone) => setSettings({ timeZone })}
              showSearch={{ optionFilterProp: 'label' }}
              options={timeZones.map((value) => ({ value, label: value }))}
            />
          </div>
        </Section>
        <hr />
        <Section title={t('其他设置', 'Other settings')}>
          <div className={styles.row}>
            <span>{t('色弱模式', 'Color weakness mode')}</span>
            <Switch
              aria-label={t('色弱模式', 'Color weakness mode')}
              checked={Boolean(settings.colorWeak)}
              onChange={(colorWeak) => setSettings({ colorWeak })}
              size="small"
            />
          </div>
        </Section>
      </div>
    </Drawer>
  );
}
